import type {
  AttributionControl,
  FilterSpecification,
  GeoJSONSource,
  Map as MapLibreMap,
} from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import type { ColorScheme, MapTheme } from '../theme/palette'
import { LOCATION_VIEW, MAP_VIEW, resolveBasemapStyleUrl } from './config'
import {
  buildLayers,
  buildLocationLayers,
  EMPTY_COLLECTION,
  EMPTY_LOCATION,
  LAYER_IDS,
  LOCATION_SOURCE_ID,
  type LocationCollection,
  locationSourceSpec,
  STATIONS_SOURCE_ID,
  type StationsCollection,
  stationsSourceSpec,
} from './layers'
import type { CameraBox, ViewportBounds } from './viewport'

/**
 * Everything that talks to MapLibre directly.
 *
 * MapLibre is loaded with a dynamic `import()`, which keeps it out of the
 * initial JavaScript chunk: the shell, the filter panel and the detail panel are
 * interactive while the map engine is still arriving, and the budget for our own
 * code is measurable on its own.
 */

export interface CreateMapOptions {
  readonly container: HTMLElement
  readonly scheme: ColorScheme
  readonly reducedMotion: boolean
  /** From `i18n`, so no copy is written here. */
  readonly label: string
  /** Credit for the charger data, on top of what the style's TileJSON declares. */
  readonly dataAttribution: string
}

export interface MapHandle {
  readonly map: MapLibreMap
  /**
   * Swap the custom attribution when the language changes.
   *
   * MapLibre has no setter for `customAttribution`, so the control is replaced.
   * The credit itself is a licence condition and must never blink out, so the
   * replacement is added before the old one is removed.
   */
  setDataAttribution(text: string): void
}

export const createMap = async ({
  container,
  scheme,
  reducedMotion,
  label,
  dataAttribution,
}: CreateMapOptions): Promise<MapHandle> => {
  // Namespaced rather than destructured: `Map` would shadow the global.
  const maplibre = await import('maplibre-gl')

  const map = new maplibre.Map({
    container,
    style: resolveBasemapStyleUrl(scheme),
    bounds: MAP_VIEW.bounds,
    fitBoundsOptions: { padding: MAP_VIEW.fitPadding },
    minZoom: MAP_VIEW.minZoom,
    maxZoom: MAP_VIEW.maxZoom,
    // Rotation buys nothing on a point map and costs a whole gesture vocabulary.
    dragRotate: false,
    pitchWithRotate: false,
    // Cross-fading tiles is motion too.
    fadeDuration: reducedMotion ? 0 : 300,
    attributionControl: false,
  })

  map.touchZoomRotate.disableRotation()
  map.getCanvas().setAttribute('aria-label', label)

  // Scale first so it stacks above the attribution, which stays last and
  // therefore closest to the corner.
  map.addControl(new maplibre.ScaleControl({ maxWidth: 96, unit: 'metric' }), 'bottom-right')

  /**
   * Attribution is a licence condition, so `compact: false` - it is never
   * allowed to collapse behind an "i" button that a visitor has to know to
   * press, at any viewport width. On a narrow screen the CSS wraps it onto
   * two lines instead of hiding it.
   */
  const attributionControl = (text: string): AttributionControl =>
    new maplibre.AttributionControl({ compact: false, customAttribution: text })

  let attribution = attributionControl(dataAttribution)
  map.addControl(attribution, 'bottom-right')

  return {
    map,
    setDataAttribution: (text) => {
      const next = attributionControl(text)
      map.addControl(next, 'bottom-right')
      map.removeControl(attribution)
      attribution = next
    },
  }
}

/**
 * Add the station source and layers if they are not there.
 *
 * Called on `load` and again after every basemap style swap, because
 * `setStyle` drops everything the style did not declare.
 */
export const ensureStationLayers = (
  map: MapLibreMap,
  theme: MapTheme,
  selectedSiteId: string | null,
  data: StationsCollection = EMPTY_COLLECTION,
): void => {
  if (map.getSource(STATIONS_SOURCE_ID) === undefined)
    map.addSource(STATIONS_SOURCE_ID, stationsSourceSpec(data))

  buildLayers(theme, selectedSiteId).forEach((layer) => {
    if (map.getLayer(layer.id) === undefined) map.addLayer(layer)
  })
}

/**
 * Add the location source and its two layers if they are not there.
 *
 * Separate from {@link ensureStationLayers} but called from the same place, for
 * the same reason: `setStyle` drops every layer the style did not declare, so a
 * colour-scheme change would otherwise lose the blue dot and leave the visitor
 * looking for themselves on the map.
 */
export const ensureLocationLayers = (
  map: MapLibreMap,
  theme: MapTheme,
  data: LocationCollection = EMPTY_LOCATION,
): void => {
  if (map.getSource(LOCATION_SOURCE_ID) === undefined)
    map.addSource(LOCATION_SOURCE_ID, locationSourceSpec(data))

  buildLocationLayers(theme).forEach((layer) => {
    if (map.getLayer(layer.id) === undefined) map.addLayer(layer)
  })
}

/**
 * `getSource` is typed as the generic `Source` interface, so a GeoJSON source
 * has to be narrowed by hand. The runtime check is on the method, not on the
 * class, so a source swapped for a stub in a test still works.
 */
const geoJsonSource = (map: MapLibreMap, id: string): GeoJSONSource | undefined => {
  const source = map.getSource(id)
  if (source === undefined) return undefined
  const candidate = source as Partial<GeoJSONSource>
  return typeof candidate.setData === 'function' ? (source as GeoJSONSource) : undefined
}

const stationsSource = (map: MapLibreMap): GeoJSONSource | undefined =>
  geoJsonSource(map, STATIONS_SOURCE_ID)

export const setStationsData = (map: MapLibreMap, data: StationsCollection): void => {
  stationsSource(map)?.setData(data)
}

export const setLocationData = (map: MapLibreMap, data: LocationCollection): void => {
  geoJsonSource(map, LOCATION_SOURCE_ID)?.setData(data)
}

const SELECTION_LAYER_IDS = [LAYER_IDS.selectionHalo, LAYER_IDS.selectionRing]

/** Both selection layers share one filter; they only differ in how they draw it. */
export const setSelectionFilter = (map: MapLibreMap, filter: FilterSpecification): void => {
  SELECTION_LAYER_IDS.forEach((id) => {
    if (map.getLayer(id) !== undefined) map.setFilter(id, filter)
  })
}

export const readViewport = (
  map: MapLibreMap,
): { bounds: ViewportBounds; center: [number, number] } => {
  const bounds = map.getBounds()
  const center = map.getCenter()
  return {
    bounds: {
      west: bounds.getWest(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      north: bounds.getNorth(),
    },
    center: [center.lng, center.lat],
  }
}

export interface EaseOptions {
  readonly center: [number, number]
  readonly zoom?: number
  readonly reducedMotion: boolean
}

export const easeToSite = (
  map: MapLibreMap,
  { center, zoom, reducedMotion }: EaseOptions,
): void => {
  map.easeTo({
    center,
    zoom: zoom ?? Math.max(map.getZoom(), MAP_VIEW.selectionZoom),
    duration: reducedMotion ? 0 : MAP_VIEW.easeDurationMs,
  })
}

/**
 * Fly to the frame {@link locationViewBounds} worked out.
 *
 * `maxZoom` is the half of this that cannot be expressed in the bounds: five
 * chargers in one car park produce a box a few metres across, and without the
 * cap the map would arrive at street-furniture zoom with nothing on screen but
 * the dot it was supposed to put in context.
 */
export const frameLocation = (
  map: MapLibreMap,
  bounds: CameraBox,
  reducedMotion: boolean,
): void => {
  map.fitBounds(bounds as [[number, number], [number, number]], {
    padding: LOCATION_VIEW.fitPadding,
    maxZoom: LOCATION_VIEW.maxZoom,
    duration: reducedMotion ? 0 : MAP_VIEW.easeDurationMs,
  })
}

export const frameCountry = (map: MapLibreMap, reducedMotion: boolean): void => {
  map.fitBounds(MAP_VIEW.bounds, {
    padding: MAP_VIEW.fitPadding,
    duration: reducedMotion ? 0 : MAP_VIEW.easeDurationMs,
  })
}

/**
 * Zoom into a cluster far enough that it breaks apart. MapLibre computes the
 * exact zoom from the cluster index; the step is only a fallback for the case
 * where the cluster has already been superseded by a newer `setData`.
 */
export const expandCluster = async (
  map: MapLibreMap,
  clusterId: number,
  center: [number, number],
  reducedMotion: boolean,
): Promise<void> => {
  const source = stationsSource(map)
  let zoom = map.getZoom() + MAP_VIEW.clusterZoomStep

  if (source !== undefined) {
    try {
      zoom = await source.getClusterExpansionZoom(clusterId)
    } catch {
      // The cluster is gone, which only happens when the data changed under
      // the click. The fallback step is a perfectly good answer.
    }
  }

  map.easeTo({
    center,
    zoom,
    duration: reducedMotion ? 0 : MAP_VIEW.easeDurationMs,
  })
}
